import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Edit2, Save, X, History, DollarSign, 
  AlertCircle, CheckCircle, Clock, Plus, Trash2, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import AppHeader from '@/components/shared/AppHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentCorrectionSystem = ({ user, onLogout }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedFields, setEditedFields] = useState({});
  const [correctionReason, setCorrectionReason] = useState('');
  
  // Dialogs
  const [addPaymentOpen, setAddPaymentOpen] = useState(false);
  const [auditTrailOpen, setAuditTrailOpen] = useState(false);
  
  // Add payment form
  const [newPayment, setNewPayment] = useState({
    amount: '',
    payment_mode: '',
    payment_received_by: '',
    notes: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const searchBookings = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bookings/search?query=${searchQuery}`, { headers });
      setBookings(response.data);
    } catch (error) {
      // Fallback to regular bookings endpoint with filter
      try {
        const response = await axios.get(`${API}/bookings?guest_phone=${searchQuery}`, { headers });
        setBookings(response.data);
      } catch (e) {
        toast.error('Failed to search bookings');
      }
    }
    setLoading(false);
  };

  const loadPaymentDetails = async (bookingId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/payments/booking/${bookingId}`, { headers });
      setPaymentDetails(response.data);
      setSelectedBooking(response.data.booking);
      setEditedFields({});
      setEditMode(false);
    } catch (error) {
      toast.error('Failed to load payment details');
    }
    setLoading(false);
  };

  const saveCorrections = async () => {
    if (!correctionReason.trim()) {
      toast.error('Please provide a reason for the correction');
      return;
    }

    const corrections = Object.entries(editedFields).map(([field, value]) => ({
      field,
      new_value: String(value)
    }));

    if (corrections.length === 0) {
      toast.error('No changes to save');
      return;
    }

    try {
      await axios.post(`${API}/payments/bulk-correct`, {
        booking_id: selectedBooking._id,
        corrections,
        reason: correctionReason
      }, { headers });
      
      toast.success('Corrections saved successfully');
      setCorrectionReason('');
      setEditMode(false);
      loadPaymentDetails(selectedBooking._id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save corrections');
    }
  };

  const addPayment = async () => {
    if (!newPayment.amount || !newPayment.payment_mode || !newPayment.payment_received_by) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      await axios.post(`${API}/payments/add-payment`, {
        booking_id: selectedBooking._id,
        ...newPayment,
        amount: parseFloat(newPayment.amount)
      }, { headers });
      
      toast.success('Payment added successfully');
      setAddPaymentOpen(false);
      setNewPayment({ amount: '', payment_mode: '', payment_received_by: '', notes: '' });
      loadPaymentDetails(selectedBooking._id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add payment');
    }
  };

  const deletePaymentEntry = async (entryId) => {
    const reason = prompt('Reason for deleting this payment entry:');
    if (!reason) return;

    try {
      await axios.delete(`${API}/payments/payment-entry/${entryId}?reason=${encodeURIComponent(reason)}`, { headers });
      toast.success('Payment entry deleted');
      loadPaymentDetails(selectedBooking._id);
    } catch (error) {
      toast.error('Failed to delete payment entry');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'partial': return 'bg-yellow-100 text-yellow-800';
      case 'pending': return 'bg-red-100 text-red-800';
      case 'complimentary': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader title="Payment Corrections" showBack onLogout={onLogout} />
      
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Search Section */}
        <div className="glass rounded-2xl p-6">
          <h2 className="text-lg font-semibold mb-4">Search Booking</h2>
          <div className="flex gap-4">
            <Input
              placeholder="Search by guest phone or booking ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchBookings()}
              className="flex-1"
              data-testid="payment-search-input"
            />
            <Button onClick={searchBookings} disabled={loading} data-testid="payment-search-btn">
              Search
            </Button>
          </div>
          
          {/* Search Results */}
          {bookings.length > 0 && (
            <div className="mt-4 space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground">Results ({bookings.length})</h3>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {bookings.map((booking) => (
                  <div 
                    key={booking._id}
                    className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer flex justify-between items-center"
                    onClick={() => loadPaymentDetails(booking._id)}
                    data-testid={`booking-result-${booking._id}`}
                  >
                    <div>
                      <p className="font-medium">{booking.guest_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {booking.therapy_type} • {booking.booking_date} • ₹{booking.total_amount}
                      </p>
                    </div>
                    <Badge className={getStatusColor(booking.payment_status)}>
                      {booking.payment_status || 'pending'}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Payment Details Section */}
        {selectedBooking && paymentDetails && (
          <div className="glass rounded-2xl p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-lg font-semibold">{selectedBooking.guest_name}</h2>
                <p className="text-sm text-muted-foreground">
                  {selectedBooking.therapy_type} • {selectedBooking.booking_date}
                </p>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setAuditTrailOpen(true)}
                  data-testid="view-audit-btn"
                >
                  <History className="w-4 h-4 mr-1" /> Audit Trail
                </Button>
                {!editMode ? (
                  <Button 
                    size="sm" 
                    onClick={() => setEditMode(true)}
                    data-testid="edit-payment-btn"
                  >
                    <Edit2 className="w-4 h-4 mr-1" /> Edit
                  </Button>
                ) : (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => { setEditMode(false); setEditedFields({}); }}
                  >
                    <X className="w-4 h-4 mr-1" /> Cancel
                  </Button>
                )}
              </div>
            </div>

            {/* Editable Fields */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              {/* Payment Status */}
              <div>
                <Label>Payment Status</Label>
                {editMode ? (
                  <Select 
                    value={editedFields.payment_status ?? selectedBooking.payment_status}
                    onValueChange={(v) => setEditedFields({...editedFields, payment_status: v})}
                  >
                    <SelectTrigger data-testid="edit-payment-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="partial">Partial</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="complimentary">Complimentary</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge className={getStatusColor(selectedBooking.payment_status)}>
                    {selectedBooking.payment_status || 'pending'}
                  </Badge>
                )}
              </div>

              {/* Payment Mode */}
              <div>
                <Label>Payment Mode</Label>
                {editMode ? (
                  <Select 
                    value={editedFields.payment_mode ?? selectedBooking.payment_mode ?? ''}
                    onValueChange={(v) => setEditedFields({...editedFields, payment_mode: v})}
                  >
                    <SelectTrigger data-testid="edit-payment-mode">
                      <SelectValue placeholder="Select mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm mt-1">{selectedBooking.payment_mode || 'Not set'}</p>
                )}
              </div>

              {/* Payment Received By */}
              <div>
                <Label>Received By</Label>
                {editMode ? (
                  <Select 
                    value={editedFields.payment_received_by ?? selectedBooking.payment_received_by ?? ''}
                    onValueChange={(v) => setEditedFields({...editedFields, payment_received_by: v})}
                  >
                    <SelectTrigger data-testid="edit-received-by">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="spa">Spa</SelectItem>
                      <SelectItem value="hotel">Hotel</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm mt-1">{selectedBooking.payment_received_by || 'Not set'}</p>
                )}
              </div>

              {/* Base Price */}
              <div>
                <Label>Base Price (₹)</Label>
                {editMode ? (
                  <Input
                    type="number"
                    value={editedFields.base_price ?? selectedBooking.base_price ?? ''}
                    onChange={(e) => setEditedFields({...editedFields, base_price: e.target.value})}
                    data-testid="edit-base-price"
                  />
                ) : (
                  <p className="text-sm mt-1">₹{selectedBooking.base_price || 0}</p>
                )}
              </div>

              {/* Discount */}
              <div>
                <Label>Discount (%)</Label>
                {editMode ? (
                  <Input
                    type="number"
                    value={editedFields.discount_percent ?? selectedBooking.discount_percent ?? ''}
                    onChange={(e) => setEditedFields({...editedFields, discount_percent: e.target.value})}
                    data-testid="edit-discount"
                  />
                ) : (
                  <p className="text-sm mt-1">{selectedBooking.discount_percent || 0}%</p>
                )}
              </div>

              {/* Total Amount */}
              <div>
                <Label>Total Amount (₹)</Label>
                {editMode ? (
                  <Input
                    type="number"
                    value={editedFields.total_amount ?? selectedBooking.total_amount ?? ''}
                    onChange={(e) => setEditedFields({...editedFields, total_amount: e.target.value})}
                    data-testid="edit-total-amount"
                  />
                ) : (
                  <p className="text-lg font-semibold mt-1">₹{selectedBooking.total_amount || 0}</p>
                )}
              </div>
            </div>

            {/* Correction Reason (when editing) */}
            {editMode && Object.keys(editedFields).length > 0 && (
              <div className="mb-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <Label className="text-yellow-800">Reason for Correction (Required)</Label>
                <Textarea
                  placeholder="Explain why these corrections are being made..."
                  value={correctionReason}
                  onChange={(e) => setCorrectionReason(e.target.value)}
                  className="mt-2"
                  data-testid="correction-reason"
                />
                <Button 
                  className="mt-3" 
                  onClick={saveCorrections}
                  disabled={!correctionReason.trim()}
                  data-testid="save-corrections-btn"
                >
                  <Save className="w-4 h-4 mr-1" /> Save Corrections
                </Button>
              </div>
            )}

            {/* Payment History */}
            <div className="border-t pt-4 mt-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-medium">Payment History</h3>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setAddPaymentOpen(true)}
                  data-testid="add-payment-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Payment
                </Button>
              </div>
              
              {paymentDetails.payment_history?.length > 0 ? (
                <div className="space-y-2">
                  {paymentDetails.payment_history.map((entry, idx) => (
                    <div key={entry._id || idx} className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium">₹{entry.amount}</p>
                        <p className="text-sm text-muted-foreground">
                          {entry.payment_mode} • {entry.payment_received_by} • {new Date(entry.timestamp).toLocaleDateString()}
                        </p>
                        {entry.notes && <p className="text-xs text-muted-foreground">{entry.notes}</p>}
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => deletePaymentEntry(entry._id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No payment history recorded</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add Payment Dialog */}
      <Dialog open={addPaymentOpen} onOpenChange={setAddPaymentOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Payment Entry</DialogTitle>
            <DialogDescription>Record a new payment for this booking</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                value={newPayment.amount}
                onChange={(e) => setNewPayment({...newPayment, amount: e.target.value})}
                placeholder="Enter amount"
                data-testid="new-payment-amount"
              />
            </div>
            <div>
              <Label>Payment Mode</Label>
              <Select 
                value={newPayment.payment_mode}
                onValueChange={(v) => setNewPayment({...newPayment, payment_mode: v})}
              >
                <SelectTrigger data-testid="new-payment-mode">
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Received By</Label>
              <Select 
                value={newPayment.payment_received_by}
                onValueChange={(v) => setNewPayment({...newPayment, payment_received_by: v})}
              >
                <SelectTrigger data-testid="new-payment-received-by">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="spa">Spa</SelectItem>
                  <SelectItem value="hotel">Hotel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={newPayment.notes}
                onChange={(e) => setNewPayment({...newPayment, notes: e.target.value})}
                placeholder="Any additional notes..."
              />
            </div>
            <Button onClick={addPayment} className="w-full" data-testid="submit-new-payment">
              Add Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit Trail Dialog */}
      <Dialog open={auditTrailOpen} onOpenChange={setAuditTrailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Audit Trail</DialogTitle>
            <DialogDescription>History of all corrections made to this booking</DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-3">
            {paymentDetails?.audit_trail?.length > 0 ? (
              paymentDetails.audit_trail.map((entry, idx) => (
                <div key={entry._id || idx} className="p-3 border rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <Badge variant="outline">{entry.action}</Badge>
                      <p className="text-sm mt-1">
                        <span className="font-medium">{entry.corrected_by_name}</span>
                        <span className="text-muted-foreground"> • {new Date(entry.timestamp).toLocaleString()}</span>
                      </p>
                    </div>
                  </div>
                  {entry.changes ? (
                    <div className="mt-2 text-sm">
                      {entry.changes.map((change, i) => (
                        <p key={i}>
                          <span className="font-medium">{change.field}:</span>{' '}
                          <span className="text-red-600 line-through">{change.old_value}</span>{' '}
                          → <span className="text-green-600">{change.new_value}</span>
                        </p>
                      ))}
                    </div>
                  ) : entry.field && (
                    <p className="mt-2 text-sm">
                      <span className="font-medium">{entry.field}:</span>{' '}
                      <span className="text-red-600 line-through">{entry.old_value}</span>{' '}
                      → <span className="text-green-600">{entry.new_value}</span>
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    <span className="font-medium">Reason:</span> {entry.reason}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-center text-muted-foreground py-8">No audit trail entries</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentCorrectionSystem;
